import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
} from '@mui/material';
import { PageLayout } from '../../components/footer/PageLayout';
import { ContentSection, StatsDisplay } from '../../components/footer/ContentSection';
import { CTASection } from '../../components/footer/CTASection';

export const EarningsPage: React.FC = () => {
  const theme = useTheme();

  const earnings = [
    {
      value: "$75-150",
      label: "Per Hour",
      description: "Average hourly rate",
    },
    {
      value: "$3,000-6,000",
      label: "Per Month",
      description: "Part-time (20 hours/week)",
    },
    {
      value: "$6,000-12,000",
      label: "Per Month",
      description: "Full-time (40 hours/week)",
    },
    {
      value: "85%",
      label: "You Keep",
      description: "Of your earnings",
    },
  ];

  const specialties = [
    {
      specialty: "Physical Therapist",
      hourly: "$80-120",
      monthly: "$6,400-9,600",
    },
    {
      specialty: "Occupational Therapist",
      hourly: "$75-110",
      monthly: "$6,000-8,800",
    },
    {
      specialty: "Registered Nurse",
      hourly: "$70-100",
      monthly: "$5,600-8,000",
    },
    {
      specialty: "Speech Therapist",
      hourly: "$85-125",
      monthly: "$6,800-10,000",
    },
  ];

  return (
    <PageLayout
      title="Earning Potential"
      subtitle="Discover the earning potential for healthcare professionals on our platform. Set your own rates and build a successful practice."
      breadcrumbs={[
        { label: "For Caregivers", href: "/for-caregivers" },
        { label: "Earning Potential" },
      ]}
    >
      {/* Earnings Overview */}
      <ContentSection
        title="Your Earning Potential"
        subtitle="See what you can earn as a healthcare professional on our platform. These are estimates based on current market rates and platform data."
        backgroundColor={theme.palette.grey[50]}
      >
        <StatsDisplay stats={earnings} />
      </ContentSection>

      {/* Specialty Breakdown */}
      <ContentSection
        title="Earnings by Specialty"
        subtitle="Earning potential varies by specialty, experience, and location. Here are some examples:"
      >
        <Grid container spacing={3}>
          {specialties.map((specialty, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box
                sx={{
                  p: 3,
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  textAlign: 'center',
                  backgroundColor: 'white',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    color: theme.palette.primary.main,
                  }}
                >
                  {specialty.specialty}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Hourly:</strong> {specialty.hourly}
                </Typography>
                <Typography variant="body2">
                  <strong>Monthly:</strong> {specialty.monthly}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* CTA Section */}
      <CTASection
        title="Ready to Start Earning?"
        subtitle="Join our network of healthcare professionals and start building your practice today."
        primaryButton={{
          text: "Apply Now",
          href: "/auth?role=caregiver",
        }}
        secondaryButton={{
          text: "Learn More",
          href: "/become-caregiver",
          variant: "outlined",
        }}
      />
    </PageLayout>
  );
};
